import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useForm, useFieldArray } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Accordion } from "@/components/ui/accordion";
import { Card, CardContent } from "@/components/ui/card";
import TripHub from "@/components/trips/wizard/TripHub";
import RoutesAccordion from "@/components/trips/wizard/RoutesAccordion";
import DatesAccordion from "@/components/trips/wizard/DatesAccordion";
import PreferencesAccordion from "@/components/trips/wizard/PreferencesAccordion";
import PassengersAccordion from "@/components/trips/wizard/PassengersAccordion";
import { tripsApi, aiApi } from "@/lib/api";
import { createTripSchema, type CreateTripInput } from "@/lib/validations";
import {
  normalizeParseError,
  type AccordionSection,
  type ParseErrorState,
  type ParseTripResponse,
} from "@/components/trips/wizard/types";

// Normalize AI-returned dates into the YYYY-MM-DD format HTML date inputs need.
function formatDateForInput(dateStr?: string): string {
  if (!dateStr) return "";
  try {
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return dateStr;
    let date: Date;
    if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(dateStr)) {
      const [month, day, year] = dateStr.split("/");
      date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    } else {
      date = new Date(dateStr);
    }
    if (isNaN(date.getTime())) return "";
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  } catch {
    return "";
  }
}

// Async parse-job polling: ask every 2.5s, give up after ~165s.
const POLL_INTERVAL_MS = 2500;
const POLL_TIMEOUT_MS = 165_000;

const URL_RE = /https?:\/\/\S+/i;

const defaultRoute = {
  origin: "",
  destination: "",
  departureDate: "",
  timeConstraint: { type: "any" as const },
  position: 0,
  earliest_departure: "",
  latest_arrival: "",
  direct_flights_only: false,
  preferred_airlines: [] as string[],
};

const defaultPreferences = {
  direct_only: false,
  prefer_widebody: false,
  arrive_late: false,
  prefer_low_occupancy: false,
  prefer_changeable: false,
  carrier_filter: { mode: "include" as const, carriers: [] as string[] },
  price_sensitivity: 50,
};

const ALL_SECTIONS: AccordionSection[] = ["routes", "dates", "preferences", "passengers"];

export default function CreateTripWizard() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const {
    register,
    handleSubmit,
    control,
    watch,
    setValue,
    getValues,
    formState: { errors },
  } = useForm<CreateTripInput>({
    resolver: zodResolver(createTripSchema),
    mode: "onChange",
    defaultValues: {
      name: "",
      description: "",
      routes: [{ ...defaultRoute }],
      preferences: { ...defaultPreferences },
      passengers: 1,
    },
  });

  const fieldArray = useFieldArray({ control, name: "routes" });
  const { replace } = fieldArray;

  const [description, setDescription] = useState("");
  const [parsing, setParsing] = useState(false);
  const [progressMessage, setProgressMessage] = useState<string | null>(null);
  const [parseError, setParseError] = useState<ParseErrorState | null>(null);
  const [coachNotes, setCoachNotes] = useState<string | null>(null);
  const [routeLabels, setRouteLabels] = useState<string[]>([]);
  // Controlled multi-accordion open state (all collapsed initially).
  const [openSections, setOpenSections] = useState<string[]>([]);

  // Track the live poll interval so we can always clear it (unmount/done/error).
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  useEffect(() => {
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, []);

  // Open sections whose REQUIRED fields are still blank after a parse.
  function autoOpenBlanks(values: CreateTripInput) {
    const open = new Set<string>();
    const routes = values.routes || [];
    const routesIncomplete =
      routes.length === 0 ||
      routes.some((r) => !r.origin || !r.destination || !r.departureDate);
    if (routesIncomplete) {
      open.add("routes");
      // Dates is the place to fix a missing departure date specifically.
      if (routes.some((r) => !r.departureDate)) open.add("dates");
    }
    setOpenSections(Array.from(open));
  }

  // Apply a successful parse result to the form (unchanged mapping logic).
  function applyParseResult(result: ParseTripResponse, desc: string) {
    setValue("description", result.description || desc, { shouldValidate: true });
    if (result.name) setValue("name", result.name, { shouldValidate: true });
    if (result.coachNotes) setCoachNotes(result.coachNotes);

    // Party size the parser inferred ("my wife and I" = 2). Without this the
    // count silently stayed 1 even when the description clearly implied more.
    if (typeof result.passengers === "number" && result.passengers >= 1) {
      setValue("passengers", result.passengers, { shouldValidate: true });
    }

    if (result.routes && result.routes.length > 0) {
      setRouteLabels(result.routes.map((r) => r.leg_label || ""));
      // origin_assumed is a trip-level signal (the home airport was guessed).
      // The guessed origin only applies to legs departing FROM home, so flag
      // only routes whose origin matches the first (outbound) leg's origin —
      // never the return legs, whose origin (the cruise port) was stated.
      const homeOrigin = (result.routes[0]?.origin || "").toUpperCase();
      replace(
        result.routes.map((route, i) => ({
          origin: (route.origin || "").toUpperCase(),
          destination: (route.destination || "").toUpperCase(),
          departureDate: formatDateForInput(route.departureDate),
          timeConstraint: { type: "any" as const },
          position: i,
          earliest_departure: route.earliest_departure || "",
          latest_arrival: route.latest_arrival || "",
          direct_flights_only: route.direct_flights_only || false,
          preferred_airlines: [] as string[],
          origin_assumed:
            !!result.origin_assumed && (route.origin || "").toUpperCase() === homeOrigin,
        }))
      );
    }

    // Map carrierFilter -> preferences.carrier_filter (airlines -> carriers).
    if (result.carrierFilter) {
      setValue("preferences.carrier_filter", {
        mode: result.carrierFilter.mode || "include",
        carriers: result.carrierFilter.airlines || [],
      });
    }

    // Auto-open only the accordions that still have blank required fields.
    autoOpenBlanks(getValues());
  }

  // Treat any parse failure the same way: surface the message, never dead-end.
  function handleParseFailure(err: unknown) {
    const normalized = normalizeParseError(err);
    setParseError(normalized);
    // Never dead-end: expand everything so the user can fill it in manually.
    setOpenSections([...ALL_SECTIONS]);
  }

  function stopPolling() {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }

  async function handlePlan() {
    const desc = description.trim();
    if (desc.length < 10) {
      setParseError({
        code: "PARSE_FAILED",
        message: "Add a few more details about your trip (at least 10 characters).",
      });
      return;
    }

    stopPolling();
    setParsing(true);
    setParseError(null);
    setCoachNotes(null);
    setProgressMessage(
      URL_RE.test(desc) ? "Reading your link…" : "Researching your trip…"
    );

    let jobId: string;
    try {
      const started = await aiApi.parseTrip(desc);
      jobId = started.jobId;
      if (!jobId) throw new Error("No job id returned");
    } catch (err) {
      handleParseFailure(err);
      setParsing(false);
      setProgressMessage(null);
      return;
    }

    const deadline = Date.now() + POLL_TIMEOUT_MS;
    const finish = () => {
      stopPolling();
      setParsing(false);
      setProgressMessage(null);
    };

    pollRef.current = setInterval(async () => {
      if (Date.now() > deadline) {
        finish();
        setParseError(
          normalizeParseError({
            response: {
              data: {
                error: {
                  code: "AI_SERVICE_DOWN",
                  message:
                    "The trip planner is taking longer than expected. Open the sections below to enter your trip manually.",
                },
              },
            },
          })
        );
        setOpenSections([...ALL_SECTIONS]);
        return;
      }
      try {
        const job = await aiApi.getParseJob(jobId);
        if (job.status === "pending") return;
        if (job.status === "done") {
          finish();
          applyParseResult(job.result as ParseTripResponse, desc);
          return;
        }
        // status === 'error'
        finish();
        handleParseFailure({ response: { data: { error: job.error } } });
      } catch (err) {
        // A failed poll (e.g. 404/network) ends the attempt cleanly.
        finish();
        handleParseFailure(err);
      }
    }, POLL_INTERVAL_MS);
  }

  const mutation = useMutation({
    mutationFn: (data: CreateTripInput) => {
      const transformed = {
        name: data.name,
        description: data.description || "",
        routes: data.routes.map((r, i) => ({
          ...r,
          position: i,
          earliest_departure: r.earliest_departure || undefined,
          latest_arrival: r.latest_arrival || undefined,
          preferred_airlines: r.preferred_airlines?.length ? r.preferred_airlines : [],
        })),
        preferences: data.preferences,
        passengers: data.passengers,
      };
      return tripsApi.create(transformed);
    },
    onSuccess: (data: any) => {
      queryClient.invalidateQueries({ queryKey: ["trips"] });
      const tripId = data?.trip?.id || data?.id;
      // The server starts a fare check on creation and returns its jobId — pass
      // it through as ?check=… so the trip page opens already showing live
      // "Checking fares…" and renders results ASAP (no manual Check Prices click).
      const checkJobId = data?.checkJobId;
      const suffix = checkJobId ? `?check=${checkJobId}` : "";
      navigate(tripId ? `/trips/${tripId}${suffix}` : "/dashboard");
    },
  });

  function onSubmit(data: CreateTripInput) {
    mutation.mutate(data);
  }

  // Validation errors should reveal the offending section.
  function onInvalid() {
    const open = new Set(openSections);
    // The Trip name field lives inside the Routes accordion — reveal it so the
    // "Trip name is required" message is never a silent no-op.
    if (errors.name) {
      open.add("routes");
    }
    if (errors.routes) {
      open.add("routes");
      open.add("dates");
    }
    setOpenSections(Array.from(open));
  }

  const routeCount = fieldArray.fields.length;

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Plan a trip</h1>
        <p className="text-sm text-muted-foreground">
          Describe it once — we fill in the details and you tweak the blanks.
        </p>
      </div>

      <TripHub
        description={description}
        onDescriptionChange={(value) => {
          setDescription(value);
          // Persist the typed description into RHF immediately so submitting
          // WITHOUT clicking "Plan my trip" still saves what the user wrote.
          setValue("description", value, { shouldValidate: true });
        }}
        onPlan={handlePlan}
        parsing={parsing}
        progressMessage={progressMessage}
        coachNotes={coachNotes}
        error={parseError}
      />

      <form onSubmit={handleSubmit(onSubmit, onInvalid)} className="space-y-6">
        <Accordion
          type="multiple"
          value={openSections}
          onValueChange={setOpenSections}
          className="space-y-3"
        >
          <RoutesAccordion
            register={register}
            errors={errors}
            watch={watch}
            setValue={setValue}
            fieldArray={fieldArray}
            routeLabels={routeLabels}
          />
          <DatesAccordion
            register={register}
            errors={errors}
            watch={watch}
            setValue={setValue}
            routeCount={routeCount}
            routeLabels={routeLabels}
          />
          <PreferencesAccordion watch={watch} setValue={setValue} />
          <PassengersAccordion watch={watch} setValue={setValue} />
        </Accordion>

        {mutation.isError && (
          <Card className="border-destructive">
            <CardContent className="p-4">
              <p className="text-sm text-destructive" role="alert">
                {mutation.error instanceof Error
                  ? mutation.error.message
                  : "Failed to create trip. Please try again."}
              </p>
            </CardContent>
          </Card>
        )}

        <div className="flex items-center gap-3">
          <Button type="submit" disabled={mutation.isPending}>
            {mutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />}
            {mutation.isPending ? "Creating…" : "Create trip"}
          </Button>
          <Button type="button" variant="outline" onClick={() => navigate("/dashboard")}>
            Cancel
          </Button>
        </div>
      </form>
    </div>
  );
}
